import { Suspense, lazy } from 'react';
import { Navigate, useRoutes, useLocation } from 'react-router-dom';
// layouts
import DashboardLayout from '../layouts/dashboard';
import LogoOnlyLayout from '../layouts/LogoOnlyLayout';
// components
import LoadingScreen from '../components/LoadingScreen';
// guards
import GuestGuard from '../guards/GuestGuard';
import AuthGuard from '../guards/AuthGuard';
import RoleBasedGuard from '../guards/RoleBasedGuard';

// ----------------------------------------------------------------------

const Loadable = (Component) => (props) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { pathname } = useLocation();
  const isDashboard = pathname.includes('/dashboard');

  return (
    <Suspense
      fallback={
        <LoadingScreen
          sx={{
            ...(!isDashboard && {
              top: 0,
              left: 0,
              width: 1,
              zIndex: 9999,
              position: 'fixed'
            })
          }}
        />
      }
    >
      <Component {...props} />
    </Suspense>
  );
};

export default function Router() {
  return useRoutes([
    {
      path: 'auth',
      children: [
        {
          path: '', element: <Navigate to="/auth/login" replace />
        },
        {
          path: 'login',
          element: (
            <GuestGuard>
              <Login />
            </GuestGuard>
          )
        },
        {
          path: 'register',
          element: (
            <GuestGuard>
              <Register />
            </GuestGuard>
          )
        },
        {
          path: 'reset-password',
          children: [
            {path: '', element: <GuestGuard><ResetPassword /></GuestGuard> },
            { path: ':token', element: <GuestGuard><ResetPassword /></GuestGuard> },
          ]
        },
      ]
    },
     // Dashboard Routes
     {
      path: 'dashboard',
      element: (
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
      ),
      children: [
        { path: '', element: <GeneralApp /> },
        { path: 'user',
          children: [
            { path:'', element: <Navigate to="/dashboard/user/list" replace /> },
            { path: 'list', element: <RoleBasedGuard accessibleRoles={['SUPER_ADMIN', 'AMBASSADOR']}> <UserList /> </RoleBasedGuard>},
            { path: 'new', element: <RoleBasedGuard accessibleRoles={['SUPER_ADMIN']}> <UserCreate /> </RoleBasedGuard> },
            { path: ':userId/edit', element: <RoleBasedGuard accessibleRoles={['SUPER_ADMIN']}>  <UserCreate /> </RoleBasedGuard> },
          ]
        },
        { path: 'question', element: <RoleBasedGuard accessibleRoles={['SUPER_ADMIN']}> <QuestionList /> </RoleBasedGuard> },
        { path: 'truth', element: <RoleBasedGuard accessibleRoles={['SUPER_ADMIN']}> <TruthList /> </RoleBasedGuard>},
        { path: 'idea',
          children: [
            { path:'', element: <Navigate to="/dashboard/idea/list" replace /> },
            { path: 'list', element: <RoleBasedGuard accessibleRoles={['SUPER_ADMIN', 'AMBASSADOR']}> <IdeaList /> </RoleBasedGuard>},
            { path: 'new', element: <RoleBasedGuard accessibleRoles={['SUPER_ADMIN', 'AMBASSADOR']}> <IdeaCreate /> </RoleBasedGuard> },
            { path: ':ideaId/edit', element: <RoleBasedGuard accessibleRoles={['SUPER_ADMIN', 'AMBASSADOR']}>  <IdeaCreate /> </RoleBasedGuard> },
          ]
        },
        { path: 'options',
          children: [
            { path:'', element: <Navigate to="/dashboard/options/group" replace /> },
            { path: 'group', element: <RoleBasedGuard accessibleRoles={['SUPER_ADMIN']}> <OptionGroupList /> </RoleBasedGuard>},
            { path: 'value', element: <RoleBasedGuard accessibleRoles={['SUPER_ADMIN']}> <OptionValueList /> </RoleBasedGuard> },
          ]
        },
        { path: 'sub-group', element: <RoleBasedGuard accessibleRoles={['SUPER_ADMIN']}> <SubGroupList /> </RoleBasedGuard>},
        { path: 'avatar', element: <RoleBasedGuard accessibleRoles={['SUPER_ADMIN']}> <AvatarList /> </RoleBasedGuard> },
        { path: 'emoji', element: <RoleBasedGuard accessibleRoles={['SUPER_ADMIN']}> <EmojiList /> </RoleBasedGuard> },
        { path: 'media', element: <RoleBasedGuard accessibleRoles={['SUPER_ADMIN', 'AMBASSADOR']}> <MediaPage /> </RoleBasedGuard> },
        { path: 'level', element: <RoleBasedGuard accessibleRoles={['SUPER_ADMIN']}> <LevelList /> </RoleBasedGuard> },
        { path: 'wallet-token', element: <RoleBasedGuard accessibleRoles={['SUPER_ADMIN']}> <TokenList /> </RoleBasedGuard> },
        { path: 'lets-go-out', element: <RoleBasedGuard accessibleRoles={['AMBASSADOR']}> <LetsGoOutList /> </RoleBasedGuard> },
        { path: 'location',
          children: [
            { path:'', element: <Navigate to="/dashboard/settings/country" replace /> },
            { path: 'country', element: <RoleBasedGuard accessibleRoles={['SUPER_ADMIN']}> <CountryList /> </RoleBasedGuard> },
            { path: 'state', element: <RoleBasedGuard accessibleRoles={['SUPER_ADMIN']}> <StateList /> </RoleBasedGuard> },
            { path: 'city', element: <RoleBasedGuard accessibleRoles={['SUPER_ADMIN']}> <CityList /> </RoleBasedGuard> },
          ]
        },
        { path: 'settings',
          children: [
            { path:'', element: <Navigate to="/dashboard/settings/account" replace /> },
            { path: 'account', element: <UserAccount /> }
          ]
        },
      ]
    },
    // Main Routes
    {
      path: '*',
      element: <LogoOnlyLayout />,
      children: [
        { path: '*', element: <Navigate to="/404" replace /> },
        { path: '404', element: <NotFound /> },
        { path: '500', element: <Page500 /> },
      ]
    },
    {
      path: '/',
      element: <Navigate to="/dashboard" replace={false} />,
    },
    { path: '*', element: <Navigate to="/404" replace /> }
  ]);
}

// Authentication
const Login = Loadable(lazy(() => import('../pages/authentication/Login')));
const Register = Loadable(lazy(() => import('../pages/authentication/Register')));
const ResetPassword = Loadable(lazy(() => import('../pages/authentication/ResetPassword')));
// Dashboard
const GeneralApp = Loadable(lazy(() => import('../pages/dashboard/GeneralApp')));
//Users
const UserCreate = Loadable(lazy(() => import('../pages/dashboard/users/UserCreate')));
const UserList = Loadable(lazy(() => import('../pages/dashboard/users/UserList')));
const UserAccount = Loadable(lazy(() => import('../pages/dashboard/users/UserAccount')));
//MediaPage
const MediaPage = Loadable(lazy(() => import('../pages/dashboard/media/MediaPage')));
//Questions
const QuestionList = Loadable(lazy(() => import('../pages/dashboard/questions/QuestionList')))
//Questions
const TruthList = Loadable(lazy(() => import('../pages/dashboard/truth/TruthList')))

//Ideais
const IdeaCreate = Loadable(lazy(() => import('../pages/dashboard/idea/IdeaCreate')))
const IdeaList = Loadable(lazy(() => import('../pages/dashboard/idea/IdeaList')))

//Avatar
const AvatarList = Loadable(lazy(() => import('../pages/dashboard/avatars/AvatarList')));
//Emoji
const EmojiList = Loadable(lazy(() => import('../pages/dashboard/emoji/EmojiList')));
//Sub Group
const SubGroupList = Loadable(lazy(() => import('../pages/dashboard/sub-group/SubGroupList')));
// //Option Group
const OptionGroupList = Loadable(lazy(() => import('../pages/dashboard/option-group/OptionGroupList')));
const OptionValueList = Loadable(lazy(() => import('../pages/dashboard/option-group/OptionValueList')));

// Level
const LevelList = Loadable(lazy(() => import('../pages/dashboard/levels/LevelList')));
// Wallet Token
const TokenList = Loadable(lazy(() => import('../pages/dashboard/wallet/TokenList')));

const LetsGoOutList = Loadable(lazy(() => import('../pages/dashboard/lets-go-out/LetsGoOutList')));

// Locations
const CountryList = Loadable(lazy(() => import('../pages/dashboard/location/CountryList')));
const StateList = Loadable(lazy(() => import('../pages/dashboard/location/StateList')));
const CityList = Loadable(lazy(() => import('../pages/dashboard/location/CityList')));

// Main
const NotFound = Loadable(lazy(() => import('../pages/Page404')));
const Page500 = Loadable(lazy(() => import('../pages/Page500')));